import React from "react";
import useCheckoutLogic from "./utils/useCheckoutLogic";
import DeliveryOptions from "./utils/DeliveryOptions";
import AddressSelection from "./utils/AddressSelection";
import MosqueSection from "./utils/MosqueSelection";
import OrderSummary from "./utils/OrderSummary";
import PaymentButton from "./utils/PaymentButton";
import PartialDeliveryDialog from "./utils/PartialDeliveryDialog";
import LoadingSpinner from "../../components/loader/Myloader";
import { toast } from "react-toastify";
import { getDiscountRate } from "../../context/cart.context";

export const CheckOut = () => {
  const {
    deliveryOption,
    setDeliveryOption,
    locations,
    selectedLocation,
    setSelectedLocation,
    selectedMosque,
    setSelectedMosque,
    donateWater,
    loading,
    cartItems,
    cartCount,
    cartGTotal,
    handleSubmit,
    fiveGallonNotice,
    shippingCharges,
    actualTotal,
    cartDiscount,
    mosquePrices,
    getDisplayPrice,
    getRawPrice,

    showMosqueConfirmation,
    setShowMosqueConfirmation,
    showBottleDepositConfirmation,
    setShowBottleDepositConfirmation,
    cartGrandTotal,
    bottleDepositAmount = 0,
    mosqueConfirmed,
    bottleDepositConfirmed,
    has5GallonBottles,
    get5GallonBottleCount,
    handleMosqueConfirm,
    handleBottleDepositConfirm,

    returnBottlesCount,
    setReturnBottlesCount,
    getBottlesNeedingDeposit,
    getTotalDepositAmount,

    bottleDepositAcknowledged,
    setBottleDepositAcknowledged,

    showPaymentConfirmation,
    pendingOrderSummary,
    cancelPaymentConfirmation,
    confirmAndProceedToPayment,

    // Partial delivery
    showPartialDeliveryDialog,
    setShowPartialDeliveryDialog,
    partialDeliverySchedule,
    setPartialDeliverySchedule,
    getFiveGallonUnitPrice,
    getFiveGallonItemId,
    getDepositEligibleQty,
    PARTIAL_DELIVERY_MIN_QTY,
    getContainerLimitWarning,
  } = useCheckoutLogic();

  const fiveGallonQty = get5GallonBottleCount();

  return (
    <div className="mx-auto mr-4 ml-4 mb-28">
      {/* Loading Overlay */}
      <div className="fixed flex justify-center items-center h-screen">
        {loading && <LoadingSpinner />}
      </div>

      {/* Page Title */}
      <span className="text-navarbarText text-3xl font-poppins font-medium mb-8">
        CheckOut - Order Summary
      </span>

      {/* Delivery Options */}
      <DeliveryOptions
        deliveryOption={deliveryOption}
        setDeliveryOption={setDeliveryOption}
        donateWater={donateWater}
      />

      {/* Address/Mosque Selection */}
      <div className="mt-3">
        {deliveryOption === "delivery" && (
          <AddressSelection
            locations={locations}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
          />
        )}
        {deliveryOption === "mosque" && (
          <MosqueSection
            selectedMosque={selectedMosque}
            setSelectedMosque={setSelectedMosque}
          />
        )}
      </div>

      {/* Order Summary */}
      <OrderSummary
        cartItems={cartItems}
        cartCount={cartCount}
        cartGTotal={cartGTotal}
        shippingCharges={shippingCharges}
        actualTotal={actualTotal}
        cartDiscount={cartDiscount}
        fiveGallonNotice={fiveGallonNotice}
        mosquePrices={mosquePrices}
        deliveryOption={deliveryOption}
        getRawPrice={getRawPrice}
        getDisplayPrice={getDisplayPrice}
        bottleDepositAmount={bottleDepositAmount}
        calculateTaxAmount={(price, quantity, amount) =>
          price * quantity * amount
        }
        fiveGallonQty={fiveGallonQty}
        fiveGallonItemId={getFiveGallonItemId()}
        partialDeliveryMinQty={PARTIAL_DELIVERY_MIN_QTY}
        partialDeliverySchedule={partialDeliverySchedule}
        onSetUpPartialDelivery={() => setShowPartialDeliveryDialog(true)}
      />

      {/* Do you have empty 5-gallon bottles? */}
      {has5GallonBottles() && (
        <div className="mx-auto w-full max-w-lg mt-6 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  💡 Do you have empty 5-gallon bottles?
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  You can reduce your deposit by telling us how many empty
                  bottles you have for refill.
                </p>
                <button
                  type="button"
                  onClick={() => setShowBottleDepositConfirmation(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                  <span className="text-sm underline">
                    Adjust Bottle Deposit ({bottleDepositAmount.toFixed(2)} AED)
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partial Delivery trigger */}
      {deliveryOption === "delivery" &&
        fiveGallonQty > PARTIAL_DELIVERY_MIN_QTY && (
          <div className="mx-auto w-full max-w-lg mt-6 mb-4">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="w-6 h-6 inline-block" />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    Want your bottles delivered in batches?
                  </h3>
                  <p className="text-sm text-gray-700 mb-3">
                    {partialDeliverySchedule
                      ? `Split into ${
                          partialDeliverySchedule.deliveries.length
                        } deliveries, starting ${new Date(
                          partialDeliverySchedule.deliveries[0].date
                        ).toLocaleDateString()}.`
                      : "Split your order into weekly deliveries at the same locked-in price."}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPartialDeliveryDialog(true)}
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    <span className="w-4 h-4 mr-2 inline-block" />
                    {partialDeliverySchedule
                      ? "Change Delivery Schedule"
                      : "Set Up Partial Delivery"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Payment Button */}
      <PaymentButton
        cartGTotal={cartGrandTotal}
        handleSubmit={handleSubmit}
        bottleDepositAmount={bottleDepositAmount}
        returnBottlesCount={returnBottlesCount}
      />

      {/* Prominent Info Box with Checkbox */}
      {has5GallonBottles() && (
        <div className="mx-auto w-full max-w-lg mt-6 mb-4">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-4 shadow-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  💡 Bottle Security Deposit Required
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  {bottleDepositAcknowledged ? (
                    <span className="text-green-700 font-medium">
                      ✅ Deposit confirmed:{" "}
                      <strong>{bottleDepositAmount.toFixed(2)} AED</strong>{" "}
                      (refundable)
                    </span>
                  ) : (
                    <>
                      You have {getDepositEligibleQty()} x 5-gallon bottles
                      {partialDeliverySchedule ? " (first delivery)" : ""}. Do
                      you have any empty bottles for refill? This can reduce
                      your deposit.
                    </>
                  )}
                </p>

                {bottleDepositAcknowledged ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-green-700 text-sm">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Deposit acknowledged
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowBottleDepositConfirmation(true)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                    >
                      Adjust
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowBottleDepositConfirmation(true)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
                  >
                    Review & Confirm Deposit ({bottleDepositAmount.toFixed(2)}{" "}
                    AED)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* MOSQUE CONFIRMATION MODAL */}
      {/* ========================================= */}
      {showMosqueConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-primaryVeryLight rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                🕌 Mosque Order Confirmation
              </h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">You are ordering water for:</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-blue-900">
                  {selectedMosque?.MosqueName || "Selected Mosque"}
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-yellow-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Important Information
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <ul className="list-disc list-inside space-y-1">
                        <li>
                          This order will be delivered directly to the mosque
                        </li>
                        <li>
                          <strong>
                            This is a donation - Mosque orders are
                            non-refundable
                          </strong>
                        </li>
                        <li>You will receive an SMS confirmation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="mosque-terms-checkbox"
                  className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  I confirm that this order is for{" "}
                  <strong>{selectedMosque?.MosqueName}</strong> and understand
                  that mosque donations are <strong>non-refundable</strong>.
                </span>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowMosqueConfirmation(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 hover:bg-darkVeryLight rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const checkbox = document.getElementById(
                    "mosque-terms-checkbox"
                  );
                  if (!checkbox?.checked) {
                    toast.error("Please confirm by checking the checkbox");
                    return;
                  }
                  handleMosqueConfirm();
                }}
                className="flex-1 flex items-center justify-center px-4 py-2 rounded-md border border-transparent bg-primary hover:bg-primaryHover text-base font-medium hover:text-textBlack text-textWhite shadow-sm transition"
              >
                Confirm & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* BOTTLE DEPOSIT CONFIRMATION MODAL */}
      {/* ========================================= */}
      {showBottleDepositConfirmation && (
        <div className="fixed inset-0 bg-textBlack bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-textWhite rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                💧 Bottle Deposit Confirmation
              </h3>
            </div>

            <p className="text-sm text-gray-600 mb-4 -mt-2">
              Adjust your deposit by entering how many empty bottles you have
              for refill
            </p>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                {partialDeliverySchedule ? (
                  <>
                    Your first delivery includes{" "}
                    <strong>
                      {getDepositEligibleQty()} x 5-Gallon bottle(s)
                    </strong>
                    . This one-time deposit covers your first batch only — later
                    deliveries are straight exchanges.
                  </>
                ) : (
                  <>
                    Your order includes{" "}
                    <strong>
                      {getDepositEligibleQty()} x 5-Gallon bottle(s)
                    </strong>
                  </>
                )}
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <label className="block text-sm font-semibold text-blue-900 mb-2">
                  How many empty 5-gallon bottles do you have for refill?
                </label>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={() =>
                      setReturnBottlesCount(Math.max(0, returnBottlesCount - 1))
                    }
                    disabled={returnBottlesCount <= 0}
                    className="w-10 h-10 flex items-center justify-center bg-white border-2 border-blue-300 rounded-lg text-blue-600 font-bold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    max={getDepositEligibleQty()}
                    value={returnBottlesCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      const maxVal = getDepositEligibleQty();
                      setReturnBottlesCount(Math.min(Math.max(0, val), maxVal));
                    }}
                    className="flex-1 text-center text-2xl font-bold text-blue-900 border-2 border-blue-300 rounded-lg py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setReturnBottlesCount(
                        Math.min(
                          getDepositEligibleQty(),
                          returnBottlesCount + 1
                        )
                      )
                    }
                    disabled={returnBottlesCount >= getDepositEligibleQty()}
                    className="w-10 h-10 flex items-center justify-center bg-white border-2 border-blue-300 rounded-lg text-blue-600 font-bold hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    +
                  </button>
                </div>
                <p className="text-xs text-blue-700 mt-2 text-center">
                  You can have 0 to {getDepositEligibleQty()} empty bottles
                </p>
                {getContainerLimitWarning() && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4 text-sm text-yellow-800">
                    {getContainerLimitWarning()}
                  </div>
                )}
              </div>

              <div className="bg-white border-2 border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-3 text-center">
                  📊 Deposit Calculation
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600">
                      {partialDeliverySchedule
                        ? "First delivery bottles:"
                        : "Total bottles ordered:"}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {getDepositEligibleQty()} bottles
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200">
                    <span className="text-gray-600">
                      Empty bottles you have:
                    </span>
                    <span className="font-semibold text-green-600">
                      − {returnBottlesCount} bottles
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b-2 border-gray-300">
                    <span className="text-gray-700 font-medium">
                      Bottles needing deposit:
                    </span>
                    <span className="font-bold text-blue-600">
                      {getBottlesNeedingDeposit()} bottles
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 bg-green-50 rounded-lg px-3 mt-2">
                    <span className="text-gray-700 font-semibold">
                      Refundable Deposit:
                    </span>
                    <span className="font-bold text-green-700 text-xl">
                      {getTotalDepositAmount()} AED
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-900 mb-2">
                  💡 Important Information:
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 mt-0.5 text-blue-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      Standard deposit:{" "}
                      <strong className="line-through">20 AED</strong> per
                      bottle
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 mt-0.5 text-green-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      <strong>
                        🎉 Special Offer: 50% OFF = 10 AED per bottle
                      </strong>
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-5 h-5 mr-2 mt-0.5 text-green-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      <strong>Fully refundable</strong> when you return empty
                      bottles
                    </span>
                  </li>
                </ul>
              </div>

              <label className="flex items-start space-x-3 cursor-pointer bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3">
                <input
                  type="checkbox"
                  id="bottle-deposit-acknowledge-checkbox"
                  className="mt-1 h-5 w-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm text-gray-800">
                  {getBottlesNeedingDeposit() > 0 ? (
                    <>
                      I understand and agree to pay a{" "}
                      <strong className="text-green-700">
                        refundable deposit of {getTotalDepositAmount()} AED
                      </strong>{" "}
                      for{" "}
                      <strong>
                        {getBottlesNeedingDeposit()} bottle
                        {getBottlesNeedingDeposit() > 1 ? "s" : ""}
                      </strong>
                      . I will get this deposit back when I return the empty
                      bottles.
                    </>
                  ) : (
                    <>
                      I confirm that I have{" "}
                      <strong>
                        {returnBottlesCount} empty bottle
                        {returnBottlesCount !== 1 ? "s" : ""}
                      </strong>{" "}
                      for refill, so{" "}
                      <strong className="text-green-700">
                        no deposit is required
                      </strong>
                      .
                    </>
                  )}
                </span>
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowBottleDepositConfirmation(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const checkbox = document.getElementById(
                    "bottle-deposit-acknowledge-checkbox"
                  );
                  if (!checkbox?.checked) {
                    toast.error("Please confirm by checking the checkbox");
                    return;
                  }

                  setBottleDepositAcknowledged(true);
                  setShowBottleDepositConfirmation(false);

                  toast.success(
                    getTotalDepositAmount() > 0
                      ? `Deposit confirmed: ${getTotalDepositAmount()} AED`
                      : "No deposit required - confirmed!"
                  );
                }}
                disabled={Boolean(getContainerLimitWarning())}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* PAYMENT CONFIRMATION MODAL */}
      {/* ========================================= */}
      {showPaymentConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-primaryVeryLight rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Proceed to Payment
              </h3>
            </div>

            <div className="mb-6">
              <div className="bg-white border-2 border-blue-200 rounded-lg p-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">
                    {pendingOrderSummary?.subtotal?.toFixed(2)} AED
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">VAT</span>
                  <span className="font-medium text-gray-900">
                    {pendingOrderSummary?.taxTotal?.toFixed(2)} AED
                  </span>
                </div>
                {pendingOrderSummary?.depositTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Refundable bottle deposit
                    </span>
                    <span className="font-medium text-gray-900">
                      {pendingOrderSummary.depositTotal.toFixed(2)} AED
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200">
                  <span className="text-gray-800 font-semibold">
                    Total amount
                  </span>
                  <span className="font-bold text-primary text-lg">
                    {pendingOrderSummary?.grandTotal?.toFixed(2)} AED
                  </span>
                </div>
              </div>

              {partialDeliverySchedule && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4 text-sm text-purple-800">
                  The full amount above will be credited to your{" "}
                  <strong>Zulal Wallet</strong>. Your{" "}
                  {partialDeliverySchedule.deliveries.length} scheduled
                  deliveries, starting{" "}
                  {new Date(
                    partialDeliverySchedule.deliveries[0].date
                  ).toLocaleDateString()}
                  , will be settled from that balance automatically.
                </div>
              )}

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm text-yellow-800">
                You will be redirected to the secure payment gateway. Please do
                not refresh or close the page until the payment is completed.
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={cancelPaymentConfirmation}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-darkVeryLight rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
              >
                <span className="w-4 h-4 inline-block" />
                Return to Checkout
              </button>
              <button
                type="button"
                onClick={confirmAndProceedToPayment}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-transparent bg-primary hover:bg-primaryHover text-base font-medium hover:text-textBlack text-textWhite shadow-sm transition"
              >
                <span className="w-4 h-4 inline-block" />
                OK, Proceed to Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* PARTIAL DELIVERY DIALOG */}
      {/* ========================================= */}
      <PartialDeliveryDialog
        isOpen={showPartialDeliveryDialog}
        onClose={() => setShowPartialDeliveryDialog(false)}
        totalQty={fiveGallonQty}
        unitPrice={getFiveGallonUnitPrice()}
        discountLabel={`${Math.round(
          getDiscountRate(fiveGallonQty) * 100
        )}% OFF`}
        existingSchedule={partialDeliverySchedule}
        onConfirm={(schedule) => setPartialDeliverySchedule(schedule)}
      />
    </div>
  );
};
